import { BorderRadius, Color, Spacing, Typography } from '@persona-id/design-tokens';
import { ArrowLeftIcon, ArrowRightIcon } from '@persona-id/design-tokens/icons/24px';
import { useState } from 'react';
import styled, { css } from 'styled-components';

import { ModalBody, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { industryCatalog, IndustryName } from 'dashboard/models/blueprint';
import {
  ModalV2Footer,
  ModalV2Header,
  ModalV2Subheader,
} from 'dashboard/views/DashboardSandboxHomeView/BlueprintsQuizV2Modal/BlueprintsQuizV2ModalLayout';
import Button from 'design-system/components/Button';

interface BlueprintsQuizV2IndustryProps {
  selectedIndustry: IndustryName;
  toPreviousScreen: () => void;
  toNextScreen: () => void;
  submit: (selectedOption: IndustryName) => void;
  closeModal: () => void;
}

export const BlueprintsQuizV2Industry = ({
  selectedIndustry,
  toPreviousScreen,
  toNextScreen,
  submit,
  closeModal,
}: BlueprintsQuizV2IndustryProps) => {
  const [selectedOption, setSelectedOption] = useState(selectedIndustry);

  const handleNextScreen = () => {
    submit(selectedOption);
    toNextScreen();
  };

  return (
    <>
      <ModalHeader onClose={closeModal}>
        <ModalV2Subheader>Step 1 of 3</ModalV2Subheader>
        <ModalV2Header>Select the industry that best describes your business</ModalV2Header>
      </ModalHeader>
      <ModalBody>
        <IndustryOptionsWrapper>
          {Object.keys(industryCatalog).map((industryName) => {
            const industry = industryName as IndustryName;
            return (
              <IndustryOption
                key={`industry-option-${industry}`}
                isSelected={selectedOption === industry}
                onClick={() => setSelectedOption(industry)}
              >
                {industryCatalog[industry].icon} {industryCatalog[industry].label}
              </IndustryOption>
            );
          })}
        </IndustryOptionsWrapper>
      </ModalBody>
      <ModalV2Footer>
        <Button
          aria-label='blueprints-quiz-v2-industry__button-back'
          width='auto'
          variant='basic'
          type='button'
          onClick={toPreviousScreen}
          prefixIcon={<ArrowLeftIcon />}
        >
          Back
        </Button>
        <Button
          aria-label='blueprints-quiz-v2-industry__button-next'
          width='auto'
          type='button'
          onClick={handleNextScreen}
          disabled={!selectedOption}
          suffixIcon={<ArrowRightIcon />}
        >
          Continue
        </Button>
      </ModalV2Footer>
    </>
  );
};

const IndustryOptionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  column-gap: ${Spacing[4]};
  row-gap: ${Spacing[4]};
`;

const IndustryOption = styled.div<{ isSelected: boolean }>`
  ${Typography.TextMedium};
  padding: ${Spacing[2]} ${Spacing[4]};
  border: 1px solid ${Color.BorderDivider};
  border-radius: ${BorderRadius.Md};
  min-width: 300px;
  cursor: pointer;

  :first-letter {
    margin-right: ${Spacing[1]};
  }

  ${({ isSelected }) =>
    isSelected &&
    css`
      background-color: ${Color.CorePurple100};
    `}
`;
